import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { useToast } from '../App';
import {
  getAIConfigs, getOllamaModels,
  aiEnrichRequirementStream, aiImproveTestCaseStream,
  aiAcceptNewCases, aiAcceptImprovement,
} from '../services/api';
import { MODELS_BY_PROVIDER, PROVIDERS } from '../constants/aiModels';
import { HiOutlineSparkles } from 'react-icons/hi';

export default function AITargetedModal({ show, onClose, mode, projectId, requirement, testCase, onAccepted }) {
  const addToast = useToast();

  // Provider config
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0.3);
  const [promptExtra, setPromptExtra] = useState('');
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [ollamaModels, setOllamaModels] = useState([]);

  // Generation state
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  // Selection (enrich mode)
  const [selectedItems, setSelectedItems] = useState(new Set());

  const timerRef = useRef(null);

  // Load saved AI configs on open
  useEffect(() => {
    if (!show) return;
    setResult(null);
    setError('');
    setPromptExtra('');
    setElapsedTime(0);
    setSelectedItems(new Set());

    getAIConfigs().then(({ data }) => {
      setSavedConfigs(data);
      const configuredProvider = data.find((c) => c.provider === 'openai') || data[0];
      if (configuredProvider) {
        const prov = configuredProvider.provider;
        setProvider(prov);
        if (configuredProvider.temperature !== undefined) setTemperature(configuredProvider.temperature);
        if (prov === 'ollama') {
          getOllamaModels().then(({ data: m }) => {
            const models = m.models || [];
            setOllamaModels(models);
            setModel(configuredProvider.model && models.includes(configuredProvider.model) ? configuredProvider.model : (models[0] || ''));
          }).catch(() => setOllamaModels([]));
        } else {
          const models = MODELS_BY_PROVIDER[prov] || [];
          const savedModel = configuredProvider.model;
          if (savedModel && models.some((m) => m.value === savedModel)) {
            setModel(savedModel);
          } else if (models.length > 0) {
            setModel(models[0].value);
          }
        }
      }
    }).catch(() => { });
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    setOllamaModels([]);
    const config = savedConfigs.find((c) => c.provider === newProvider);
    if (newProvider === 'ollama') {
      setModel('');
      if (config) {
        setTemperature(config.temperature !== undefined ? config.temperature : 0.3);
        getOllamaModels().then(({ data }) => {
          const models = data.models || [];
          setOllamaModels(models);
          setModel(config.model && models.includes(config.model) ? config.model : (models[0] || ''));
        }).catch(() => setOllamaModels([]));
      }
    } else {
      const models = MODELS_BY_PROVIDER[newProvider] || [];
      if (config) {
        const savedModel = config.model || '';
        if (savedModel && models.some((m) => m.value === savedModel)) setModel(savedModel);
        else setModel(models.length > 0 ? models[0].value : '');
        setTemperature(config.temperature !== undefined ? config.temperature : 0.3);
      } else {
        setModel(models.length > 0 ? models[0].value : '');
        setTemperature(0.3);
      }
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setResult(null);
    setError('');
    setSelectedItems(new Set());
    setElapsedTime(0);
    timerRef.current = setInterval(() => setElapsedTime((t) => t + 1), 1000);

    try {
      const payload = {
        provider,
        model: model || null,
        temperature,
        prompt_extra: promptExtra,
      };

      let response;
      if (mode === 'enrich') {
        response = await aiEnrichRequirementStream(projectId, requirement.id, payload);
      } else {
        response = await aiImproveTestCaseStream(projectId, testCase.id, payload);
      }

      if (!response.ok) {
        const text = await response.text();
        let detail = text;
        try { detail = JSON.parse(text).detail || text; } catch { /* noop */ }
        throw new Error(detail);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = buffer.replaceAll('\r\n', '\n');

        while (buffer.includes('\n\n')) {
          const idx = buffer.indexOf('\n\n');
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          for (const line of block.split('\n')) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data: ')) continue;
            let event;
            try { event = JSON.parse(trimmed.slice(6)); } catch { continue; }

            if (event.type === 'cas_test') {
              setResult((prev) => ({
                cas_tests: [...(prev?.cas_tests || []), event.data],
              }));
              setSelectedItems((prev) => new Set([...prev, event.index]));
            } else if (event.type === 'improved') {
              setResult({ improved_tc: event.data });
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
            // 'start' and 'done' — no special handling needed
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la generation IA');
    }

    clearInterval(timerRef.current);
    setLoading(false);
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      if (mode === 'enrich') {
        const toAccept = (result?.cas_tests || []).filter((_, i) => selectedItems.has(i));
        if (toAccept.length === 0) {
          addToast('warning', 'Aucun cas de test selectionne.');
          setAccepting(false);
          return;
        }
        await aiAcceptNewCases(projectId, requirement.id, { cas_tests: toAccept });
        addToast('success', `${toAccept.length} cas de test ajoute(s) a l'exigence ${requirement.exg_id}.`);
      } else {
        await aiAcceptImprovement(projectId, testCase.id, { improved_tc: result.improved_tc });
        addToast('success', `Cas de test ${testCase.ct_id} ameliore.`);
      }
      onAccepted();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      addToast('error', detail || 'Erreur lors de l\'acceptation.');
    }
    setAccepting(false);
  };

  const toggleItem = (idx) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const modelOptions = provider === 'ollama'
    ? ollamaModels.map((m) => ({ value: m, label: m }))
    : (MODELS_BY_PROVIDER[provider] || []);

  const title = mode === 'enrich'
    ? `Enrichir — ${requirement?.exg_id || ''}`
    : `Ameliorer — ${testCase?.ct_id || ''}`;

  const selectedCount = selectedItems.size;
  const totalGenerated = result?.cas_tests?.length || 0;

  return (
    <Modal show={show} onClose={onClose} title={title} icon={<HiOutlineSparkles />} size="xl" footer={
      result && !loading ? (
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Annuler</button>
          <button className="btn btn-success btn-sm" onClick={handleAccept} disabled={accepting}>
            {accepting
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Acceptation...</>
              : mode === 'enrich'
                ? `Accepter (${selectedCount} cas)`
                : 'Accepter l\'amelioration'
            }
          </button>
        </div>
      ) : null
    }>
      <div style={{ fontSize: '0.85rem' }}>
        {/* Provider selection */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          {PROVIDERS.map((p) => (
            <button
              key={p.value}
              className={`btn btn-sm ${provider === p.value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleProviderChange(p.value)}
              disabled={loading}
              style={provider === p.value ? { background: p.color, borderColor: p.color } : {}}
            >
              {p.logo && <img src={p.logo} alt="" style={{ width: 16, height: 16, marginRight: 4, borderRadius: 3 }} />}
              {p.label}
            </button>
          ))}
        </div>

        {/* Model selection */}
        <div className="row g-2 mb-3">
          <div className="col-md-8">
            <label className="form-label" htmlFor="field-ai-model" style={{ fontSize: '0.78rem', fontWeight: 500 }}>Modele</label>
            <select id="field-ai-model" className="form-select form-select-sm" value={model} onChange={(e) => setModel(e.target.value)} disabled={loading}>
              {modelOptions.map((m) => (
                <option key={m.value} value={m.value} disabled={m.disabled}>{m.label || m.value}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label" htmlFor="field-ai-temperature" style={{ fontSize: '0.78rem', fontWeight: 500 }}>Temperature: {temperature}</label>
            <input id="field-ai-temperature" type="range" className="form-range" min="0" max="1" step="0.1" value={temperature}
              onChange={(e) => setTemperature(Number.parseFloat(e.target.value))} disabled={loading} />
          </div>
        </div>

        {/* Extra instructions */}
        <div className="mb-3">
          <label className="form-label" htmlFor="field-ai-prompt-extra" style={{ fontSize: '0.78rem', fontWeight: 500 }}>Instructions supplementaires (optionnel)</label>
          <textarea
            id="field-ai-prompt-extra"
            className="form-control form-control-sm"
            rows={2}
            value={promptExtra}
            onChange={(e) => setPromptExtra(e.target.value)}
            disabled={loading}
            placeholder={mode === 'enrich'
              ? 'Ex: Focus sur les cas de securite et les cas limites...'
              : 'Ex: Detailler les etapes de navigation dans l\'interface...'
            }
          />
        </div>

        {/* Context info */}
        <div className="alert alert-light py-2 mb-3" style={{ fontSize: '0.78rem' }}>
          {mode === 'enrich' ? (
            <>
              <strong>Exigence :</strong> {requirement?.exg_id} — {requirement?.exigence}
              <br />
              <strong>Cas existants :</strong> {requirement?.test_cases?.length || 0} cas de test
            </>
          ) : (
            <>
              <strong>Cas de test :</strong> {testCase?.ct_id} — {testCase?.cas_test}
              <br />
              <strong>Exigence parente :</strong> {requirement?.exg_id} — {requirement?.exigence}
            </>
          )}
        </div>

        {/* Generate button */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <button className="btn btn-warning btn-sm" onClick={handleGenerate} disabled={loading || !model}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Generation... ({elapsedTime}s)</>
              : <><HiOutlineSparkles /> {mode === 'enrich' ? 'Generer des cas supplementaires' : 'Ameliorer ce cas de test'}</>
            }
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger py-2" style={{ fontSize: '0.8rem' }}>{error}</div>
        )}

        {/* Results — Enrich mode */}
        {mode === 'enrich' && result?.cas_tests?.length > 0 && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong style={{ fontSize: '0.82rem' }}>{totalGenerated} cas de test proposes</strong>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: '0.7rem' }}
                  onClick={() => setSelectedItems(new Set(result.cas_tests.map((_, i) => i)))}>
                  Tout cocher
                </button>
                <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: '0.7rem' }}
                  onClick={() => setSelectedItems(new Set())}>
                  Tout decocher
                </button>
              </div>
            </div>
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table className="table table-sm table-hover" style={{ fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: '30px' }}></th>
                    <th style={{ width: '80px' }}>ID</th>
                    <th>Cas de Test</th>
                    <th>Prerequis</th>
                    <th>Action</th>
                    <th>Resultat Attendu</th>
                  </tr>
                </thead>
                <tbody>
                  {result.cas_tests.map((ct, i) => (
                    <tr key={i} style={{ opacity: selectedItems.has(i) ? 1 : 0.4 }}>
                      <td>
                        <input type="checkbox" checked={selectedItems.has(i)} onChange={() => toggleItem(i)} />
                      </td>
                      <td><span className="req-id" style={{ fontSize: '0.65rem' }}>{ct.ct_id}</span></td>
                      <td>{ct.cas_test}</td>
                      <td className="small">{ct.prerequis}</td>
                      <td className="small action-text">{ct.action}</td>
                      <td className="small">{ct.resultat_attendu}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Results — Improve mode */}
        {mode === 'improve' && result?.improved_tc && (
          <div>
            <strong style={{ fontSize: '0.82rem' }} className="d-block mb-2">Comparaison avant / apres</strong>
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table className="table table-sm" style={{ fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: '130px' }}>Champ</th>
                    <th style={{ background: '#f8f9fa' }}>Avant</th>
                    <th style={{ background: '#f0fff0' }}>Apres (ameliore)</th>
                  </tr>
                </thead>
                <tbody>
                  {['cas_test', 'prerequis', 'action', 'resultat_attendu'].map((field) => {
                    const before = testCase?.[field] || '';
                    const after = result.improved_tc[field] || '';
                    const changed = before !== after;
                    const labels = {
                      cas_test: 'Titre',
                      prerequis: 'Prerequis',
                      action: 'Action',
                      resultat_attendu: 'Resultat attendu',
                    };
                    return (
                      <tr key={field}>
                        <td><strong>{labels[field]}</strong></td>
                        <td style={{ background: changed ? '#fff3cd' : '#f8f9fa', whiteSpace: 'pre-wrap' }}>{before || '(vide)'}</td>
                        <td style={{ background: changed ? '#d4edda' : '#f0fff0', whiteSpace: 'pre-wrap' }}>{after || '(vide)'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
